import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useI18n } from '../../contexts/I18nContext';
import { 
  LayoutDashboard, Users, Building2, FileText, Shield, Settings,
  TrendingUp, Wallet, LogOut, ChevronLeft, Menu, FileStack,
  ClipboardList, BarChart3, History, FileSignature, X
} from 'lucide-react';
import { Button } from '../ui/button';
import { useState, useEffect } from 'react';

const LOGO_URL = "https://customer-assets.emergentagent.com/job_1c07591d-63e3-4991-946f-f8a8e8c125c9/artifacts/i94v1fmk_IMG_0910.jpeg";

const AdminSidebar = () => {
  const { user, logout } = useAuth();
  const { t } = useI18n();
  const location = useLocation();
  const navigate = useNavigate();
  const [collapsed, setCollapsed] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);

  // Close mobile drawer on route change
  useEffect(() => { setMobileOpen(false); }, [location.pathname]);

  // Close on escape key
  useEffect(() => {
    const handler = (e) => { if (e.key === 'Escape') setMobileOpen(false); };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, []);

  const isActive = (path) => location.pathname === path || location.pathname.startsWith(path + '/');

  const adminLinks = [
    { path: '/admin', label: 'Dashboard', icon: LayoutDashboard, exact: true },
    { path: '/admin/users', label: 'Usuarios', icon: Users },
    { path: '/admin/opportunities', label: 'Oportunidades', icon: TrendingUp },
    { path: '/admin/properties', label: 'Inmuebles', icon: Building2 },
    { path: '/admin/commitments', label: 'Compromisos', icon: Wallet },
    { path: '/admin/signatures', label: 'Firmas', icon: FileSignature },
    { path: '/admin/compliance', label: 'Cumplimiento', icon: Shield },
    { path: '/admin/documents', label: 'Documentos', icon: FileText },
    { path: '/admin/templates', label: 'Plantillas', icon: FileStack },
    { path: '/admin/content', label: 'Contenido', icon: ClipboardList },
    { path: '/admin/reports', label: 'Reportes', icon: BarChart3 },
    { path: '/admin/audit-log', label: 'Auditoría', icon: History },
    { path: '/admin/settings', label: 'Configuración', icon: Settings },
  ];

  const handleLogout = async () => {
    await logout();
    navigate('/');
  };

  const sidebarContent = (
    <div className="flex flex-col h-full">
      {/* Logo */}
      <div className="h-20 flex items-center justify-between px-4 border-b border-border">
        {!collapsed && (
          <Link to="/admin">
            <img src={LOGO_URL} alt="Silver Brick" className="h-10 w-auto" />
          </Link>
        )}
        {/* Desktop: collapse toggle */}
        <button
          onClick={() => setCollapsed(!collapsed)}
          className="hidden lg:block p-2 hover:bg-muted rounded-sm transition-colors"
        >
          {collapsed ? (
            <Menu className="h-5 w-5" strokeWidth={1.5} />
          ) : (
            <ChevronLeft className="h-5 w-5" strokeWidth={1.5} />
          )}
        </button>
        {/* Mobile: close button */}
        <button
          onClick={() => setMobileOpen(false)}
          className="lg:hidden p-2 hover:bg-muted rounded-sm transition-colors"
        >
          <X className="h-5 w-5" strokeWidth={1.5} />
        </button>
      </div>

      {/* Admin Badge */}
      {!collapsed && (
        <div className="p-4 border-b border-border">
          <div className="flex items-center gap-3">
            <div className="h-10 w-10 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
              <Shield className="h-5 w-5 text-primary" strokeWidth={1.5} />
            </div>
            <div className="min-w-0">
              <p className="text-sm font-medium truncate">{user?.name}</p>
              <p className="text-xs text-primary uppercase tracking-wider">Admin</p>
            </div>
          </div>
        </div>
      )}

      {/* Navigation */}
      <nav className="flex-1 p-2 space-y-1 overflow-y-auto">
        {adminLinks.map((link) => {
          const Icon = link.icon;
          const active = link.exact 
            ? location.pathname === link.path 
            : isActive(link.path);
          return (
            <Link
              key={link.path}
              to={link.path}
              className={`flex items-center gap-3 px-3 py-2.5 rounded-sm transition-colors ${
                active
                  ? 'bg-primary text-primary-foreground'
                  : 'text-muted-foreground hover:bg-muted hover:text-foreground'
              }`}
              title={collapsed ? link.label : undefined}
            >
              <Icon className="h-4 w-4 flex-shrink-0" strokeWidth={1.5} />
              {!collapsed && <span className="text-sm">{link.label}</span>}
            </Link>
          );
        })}
      </nav>

      {/* Back to App & Logout */}
      <div className="p-2 border-t border-border space-y-1">
        <Link to="/app/dashboard">
          <Button
            variant="ghost"
            className={`w-full justify-start gap-3 text-muted-foreground hover:text-foreground ${
              collapsed ? 'px-3' : ''
            }`}
          >
            <LayoutDashboard className="h-4 w-4" strokeWidth={1.5} />
            {!collapsed && <span className="text-sm">Ir al Portal</span>}
          </Button>
        </Link>
        <Button
          variant="ghost"
          onClick={handleLogout}
          className={`w-full justify-start gap-3 text-muted-foreground hover:text-destructive ${
            collapsed ? 'px-3' : ''
          }`}
        >
          <LogOut className="h-4 w-4" strokeWidth={1.5} />
          {!collapsed && <span className="text-sm">{t('nav.logout')}</span>}
        </Button>
      </div>
    </div>
  );

  return (
    <>
      {/* Mobile top bar with hamburger */}
      <div className="lg:hidden fixed top-0 left-0 right-0 h-14 bg-card border-b border-border z-30 flex items-center px-4 gap-3">
        <button
          onClick={() => setMobileOpen(true)}
          className="p-2 hover:bg-muted rounded-sm transition-colors"
          data-testid="mobile-menu-toggle"
        >
          <Menu className="h-5 w-5" strokeWidth={1.5} />
        </button>
        <Link to="/admin">
          <img src={LOGO_URL} alt="Silver Brick" className="h-8 w-auto" />
        </Link>
        <span className="text-xs text-primary uppercase tracking-wider ml-auto">Admin</span>
      </div>

      {/* Mobile overlay */}
      {mobileOpen && (
        <div
          className="lg:hidden fixed inset-0 bg-black/60 z-40"
          onClick={() => setMobileOpen(false)}
        />
      )}

      {/* Mobile drawer */}
      <aside className={`lg:hidden fixed left-0 top-0 h-screen w-72 bg-card border-r border-border z-50 transform transition-transform duration-300 ${
        mobileOpen ? 'translate-x-0' : '-translate-x-full'
      }`}>
        {sidebarContent}
      </aside>

      {/* Desktop sidebar */}
      <aside className={`hidden lg:block fixed left-0 top-0 h-screen bg-card border-r border-border transition-all duration-300 z-40 ${
        collapsed ? 'w-16' : 'w-64'
      }`}>
        {sidebarContent}
      </aside>
    </>
  );
};

export default AdminSidebar;
